/**
 * Turning a URL into a complete scope (pure; `useFocus` runs it).
 *
 *  1. No `app_id` and no `all_apps`: the scope is the user's Focus
 *     (GET /api/admaster/focus — stored, or recommended). Its apps and, on a
 *     screen with dates, its data-anchored window are written into the URL
 *     with `replace`, so the first visit lands on the Focus app with zero
 *     clicks and no extra history entry. Anything the URL names wins over
 *     the Focus: explicit `app_id`s, explicit dates, and an explicit
 *     `period` (only the apps are taken from the Focus then).
 *  2. No dates (screens with dates only): the named `period` (or, without
 *     one, the automatic window for the selected apps, window.ts) is resolved
 *     against the scope's data bounds and written into the URL, again with
 *     `replace`.
 *  3. Otherwise the URL is complete and the screen renders it.
 *
 * Only scope keys are written; a screen's own keys pass through untouched.
 */
import type { FocusApp, FocusReplacement, FocusResponse } from './api';
import { WINDOW_OF_PERIOD, periodOfWindow, periodRange, todayUtc, type DataBounds, type Day } from './dates';
import type { FocusSearch, Period } from './search';
import { recommendedWindow } from './window';

export type Resolution<S extends FocusSearch = FocusSearch> =
  /** Waiting for the Focus or the data bounds. */
  | { kind: 'wait' }
  /** The account has no entitled app, so there is no Focus. */
  | { kind: 'none' }
  /** The URL is incomplete: replace it with `search`. */
  | { kind: 'redirect'; search: S; replaced: FocusReplacement | null }
  | { kind: 'ready' };

export interface ResolveInputs<S extends FocusSearch> {
  search: S;
  /** Whether the screen has dates (`FocusControls.dates`); default true. */
  dates?: boolean;
  /** Needed only when the URL names no app. */
  focus: FocusResponse | undefined;
  /** undefined while loading; [] when it failed. */
  focusApps: readonly FocusApp[] | undefined;
  /** undefined while loading; null when they could not be loaded (fall back to today). */
  bounds: DataBounds | null | undefined;
  today?: Day;
}

export const needsFocus = (search: FocusSearch): boolean => !search.all_apps && search.app_id.length === 0;

export function resolveSearch<S extends FocusSearch>({ search, dates = true, focus, focusApps, bounds, today = todayUtc() }: ResolveInputs<S>): Resolution<S> {
  let next = search;
  let replaced: FocusReplacement | null = null;

  if (needsFocus(search)) {
    if (!focus) return { kind: 'wait' };
    if (focus.source === 'none' || !focus.app_ids.length) return { kind: 'none' };
    replaced = focus.replaced;
    next = { ...search, app_id: [...focus.app_ids] };
    if (!dates) return { kind: 'redirect', search: next, replaced };
    // Anything named in the URL wins: an explicit `period` (e.g. a link to
    // "the last 28 days") keeps its own window, anchored in step 2 on the
    // Focus apps' data, instead of taking the Focus window's dates.
    if (!search.date_from && !search.period && focus.date_from && focus.date_to) {
      next.date_from = focus.date_from;
      next.date_to = focus.date_to;
      next.period = focus.window_days ? periodOfWindow(focus.window_days) : search.period;
    } else if (!search.date_from && !search.period && focus.window_days) {
      // The Focus names a window but no dates: step 2 anchors the backend's window.
      next.period = periodOfWindow(focus.window_days);
    }
    // Dates still missing: step 2 runs against the bounds of the NEW scope.
    if (!next.date_from || !next.date_to) return { kind: 'redirect', search: next, replaced };
  }

  if (dates && (!next.date_from || !next.date_to)) {
    // The automatic window depends on the apps' evidence: wait for the list (an error passes []).
    if (!next.period && !next.all_apps && focusApps === undefined) return { kind: 'wait' };
    const period: Period = next.period ?? periodOfWindow(recommendedWindow(next.app_id, focusApps));
    const calendar = period !== 'lifetime' && !WINDOW_OF_PERIOD[period];
    if (bounds === undefined && !calendar) return { kind: 'wait' };
    const known: DataBounds = bounds ?? { first: null, last: null };
    // Lifetime without known bounds has no honest range; fall back to the automatic window.
    const range = periodRange(period, known, today) ?? periodRange('last_14', known, today);
    if (!range) return { kind: 'wait' };
    next = { ...next, date_from: range.from, date_to: range.to, period: periodRange(period, known, today) ? period : 'last_14' };
  }

  return next === search ? { kind: 'ready' } : { kind: 'redirect', search: next, replaced };
}
